/* eslint-disable arrow-parens */
import React, { useCallback, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FormControl, Button, InputAdornment } from "@material-ui/core";
import Snackbar, { SnackbarCloseReason } from "@material-ui/core/Snackbar";
import { trackPromise } from "react-promise-tracker";
import MuiAlert from "@material-ui/lab/Alert";

// other sharable components / functions
import { ThemeCustomInputField } from "../../../../shared/themeComponents/themeCustomInputField";
import { api } from "../../../../../utils/api";
import { password_reset } from "../../../../../assets";

// logos and icon
import { nexcaliberLogo } from "../../../../../assets";
import LockIcon from "@material-ui/icons/Lock";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";

import "./resetPassword.scss";
import validatePassword from "../../../../../utils/commonFunctions/validatePassword";
import { Validation } from "../../../../../@types/validation.types";

const ResetPassword = () => {
	const [newPassword, setNewPassword] = useState("");
	const [confirmNewPassword, setConfirmNewPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [hasPasswordReset, setHasPasswordReset] = useState(false);
	const { token } = useParams();
	const [snackProps, setSnackProps] = useState({
		message: "",
		open: false,
		severity: ""
	});
	const [validation, setValidation] = useState<Validation>({
		newPassword: "",
		confirmNewPassword: "",
		status: "invalid"
	});

	const _validation = useRef<Validation>();
	const navigate = useNavigate();

	const handleValidation = useCallback(async () => {
		_validation.current = {
			...validation,
			newPassword: "",
			confirmNewPassword: "",
			status: "invalid"
		};
		let flag = true,
			confirmPasswordFlag = true,
			validPasswordFlag = true;

		if (newPassword.length === 0) {
			_validation.current["newPassword"] = "New Password is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (newPassword.length !== 0) {
			if (!validatePassword(newPassword)) {
				_validation.current["newPassword"] = "Please enter a valid password";
				_validation.current["status"] = "invalid";
				validPasswordFlag = false;
			}
		}
		if (confirmNewPassword.length === 0) {
			_validation.current["confirmNewPassword"] = "Confirm New Password is required";
			_validation.current["status"] = "invalid";
			flag = false;
		}
		if (newPassword !== confirmNewPassword) {
			_validation.current["confirmNewPassword"] = "Password doesn't match!";
			_validation.current["status"] = "invalid";
			confirmPasswordFlag = false;
		}

		if (flag === true && confirmPasswordFlag === true && validPasswordFlag === true)
			_validation.current["status"] = "valid";
		else if (confirmPasswordFlag === false) {
			_validation.current["status"] = "invalid";
			alert("Confirm New Password must be same with New Password!");
		} else if (validPasswordFlag === false) {
			_validation.current["status"] = "invalid";
			alert(
				"Password length must be at least 8 characters and contain at least one lowercase letter, one uppercase letter, one number and one special character!"
			);
		} else {
			_validation.current["status"] = "invalid";
			alert("Please, fill all required fields!");
		}
		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [confirmNewPassword, newPassword, validation]);

	const Alert = (props: any) => {
		return <MuiAlert elevation={6} variant="filled" {...props} />;
	};

	const handleShowPassword = () => setShowPassword(!showPassword);

	const handleShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

	const handleSnackbarClose = (event: React.SyntheticEvent<any, Event>, reason: SnackbarCloseReason | any) => {
		if (reason === "clickaway") return;
		setSnackProps(Object.assign({}, snackProps, { open: false }));
	};

	// input field change
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value, name } = event.target;
		if (name === "new_password") setNewPassword(value);
		else setConfirmNewPassword(value);
	};

	// RESET PASSWORD FUNCTIONALITY - BUTTON
	const handleChangePasswordClick = async (
		event: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>
	) => {
		try {
			event.preventDefault();
			const validation = await handleValidation();
			if (validation === "valid") {
				const payload = {
					new_password: newPassword
				};

				const response = await trackPromise(api.auth.changeForgetPassword(payload, token));

				if (response) {
					setSnackProps(
						Object.assign({}, snackProps, {
							open: true,
							severity: "success",
							message: "Password has been successfully reset!"
						})
					);
					setHasPasswordReset(true);

					setTimeout(() => {
						setSnackProps(
							Object.assign({}, snackProps, {
								open: false,
								severity: "",
								message: ""
							})
						);

						navigate("/login");
					}, 5000);
				}
			}
		} catch (error) {
			alert("Unable to Change Password!");
			throw error;
		}
	};

	return (
		<div className="reset-password-main-container" id="reset-password-main-container">
			<div className="left-section">
				<img className="illustration" src={password_reset} alt="" />
				<div className="tagline">
					<h3>Recoveries . . .</h3>
					<span>are very simple in our portal</span>
				</div>
			</div>
			<div className="right-section">
				<img className="nexcaliber-logo" src={nexcaliberLogo} alt="" />
				<h3>
					Password <span>Reset</span>
				</h3>
				<span className="subheading">Now you can have a new password.</span>

				<form autoComplete="off" method="">
					<FormControl className="form-container" id="form-container">
						{/* -------------------- NEW PASSWORD FIELD --------------------- */}
						<ThemeCustomInputField
							className="form-field-input"
							type={!showPassword ? "password" : "text"}
							label="Enter New Password"
							name="new_password"
							variant="outlined"
							value={newPassword}
							onChange={handleChange}
							helperText={validation["newPassword"]}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{!showPassword ? (
											<Button
												onClick={handleShowPassword}
												style={{
													padding: 3,
													marginRight: "-18px",
													borderWidth: 0,
													borderRadius: 70
												}}
											>
												<VisibilityOffIcon style={{ color: "#8C68E1" }} />
											</Button>
										) : (
											<Button
												onClick={handleShowPassword}
												style={{
													padding: 3,
													marginRight: "-18px",
													borderWidth: 0,
													borderRadius: 70
												}}
											>
												<VisibilityIcon style={{ color: "#8C68E1" }} />
											</Button>
										)}
									</InputAdornment>
								)
							}}
						/>
						{/* CONFIRM NEW PASSWORD FIELD */}
						<ThemeCustomInputField
							className="form-field-input"
							type={!showConfirmPassword ? "password" : "text"}
							label="Confirm New Password"
							name="confirm_new_password"
							variant="outlined"
							value={confirmNewPassword}
							onChange={handleChange}
							helperText={validation["confirmNewPassword"]}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										{!showConfirmPassword ? (
											<Button
												onClick={handleShowConfirmPassword}
												style={{
													padding: 3,
													marginRight: "-18px",
													borderWidth: 0,
													borderRadius: 70
												}}
											>
												<VisibilityOffIcon style={{ color: "#8C68E1" }} />
											</Button>
										) : (
											<Button
												onClick={handleShowConfirmPassword}
												style={{
													padding: 3,
													marginRight: "-18px",
													borderWidth: 0,
													borderRadius: 70
												}}
											>
												<VisibilityIcon style={{ color: "#8C68E1" }} />
											</Button>
										)}
									</InputAdornment>
								)
							}}
						/>

						<div className="reset-password-button-container" id="reset-password-button-container">
							<Button
								className="reset-password-button"
								variant="contained"
								type="submit"
								onClick={handleChangePasswordClick}
							>
								<span className="button-label-with-icon" style={{ color: "#ffff" }}>
									Change Password
								</span>
								<span>
									<LockIcon className="button-icon" style={{ color: "#ffff" }} />
								</span>
							</Button>
						</div>

						<div className="sign-in-container">
							Are you a already a user? &nbsp;
							<span className="link-container">
								<Link className="sign-in-link" to="/login">
									Sign in
								</Link>
							</span>
						</div>
					</FormControl>
				</form>
				<div className="copyright-text-container">
					<span>© 2026 nexcaliber, All rights reserved</span>
				</div>
			</div>
			<Snackbar open={snackProps.open} onClose={handleSnackbarClose}>
				<Alert onClose={handleSnackbarClose} severity={snackProps.severity}>
					{snackProps.message}
				</Alert>
			</Snackbar>
		</div>
	);
};

export default ResetPassword;
